"use client";

import Link from "next/link";
import { Button, DropdownMenu, Text } from "@radix-ui/themes";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { Auth } from "@/api/auth";
import LanguageSwitcher from "@/components/entities/header/language-switcher";
import S from "./index.module.scss";

interface UserMenuProps {
  firstName: string;
  email?: string | null;
}

const getUserInitial = (firstName: string) => {
  const trimmedFirstName = firstName.trim();

  return trimmedFirstName ? trimmedFirstName.charAt(0) : "U";
};

const UserMenu = ({ firstName, email }: UserMenuProps) => {
  const t = useTranslations("header.userMenu");
  const [logoutLoading, setLogoutLoading] = useState(false);
  const isPending = logoutLoading;

  const handleLogout = async () => {
    if (isPending) {
      return;
    }

    setLogoutLoading(true);

    try {
      await Auth.logout();
    } catch {
      setLogoutLoading(false);
      return;
    }

    window.location.replace("/sign-in");
  };

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger>
        <Button variant="soft" color="gray" size="2" className={S.trigger} aria-label={t("profileLabel")}>
          <span className={S.triggerName}>{firstName}</span>
          <span className={S.triggerBadge} aria-hidden="true">
            {getUserInitial(firstName)}
          </span>
        </Button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Content align="end" className={S.content}>
        <div className={S.userMeta}>
          <Text size="1" className={S.menuLabel}>
            {t("profileLabel")}
          </Text>
          <Text size="2" weight="medium">
            {firstName}
          </Text>
          {email ? (
            <Text size="1" color="gray">
              {email}
            </Text>
          ) : null}
        </div>

        <DropdownMenu.Separator />

        <LanguageSwitcher
          trigger={
            <DropdownMenu.Item color="gray" onSelect={event => event.preventDefault()} className={S.buttonItem}>
              {t("language")}
            </DropdownMenu.Item>
          }
        />

        <DropdownMenu.Item color="gray" asChild className={S.buttonItem}>
          <Link href="/profile">{t("editProfile")}</Link>
        </DropdownMenu.Item>

        <DropdownMenu.Item color="red" disabled={isPending} onSelect={handleLogout} className={S.dangerItem}>
          {t("logout")}
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export default UserMenu;
